import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { importApi } from '@/lib/api'
import { FinanceCsvPreviewResult, FinanceCsvApplyResult } from '@/types'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Upload, AlertCircle, CheckCircle2 } from 'lucide-react'

const FORMAT_LABELS: Record<string, string> = {
  ynab: 'YNAB',
  mint: 'Mint / Intuit',
  monarch: 'Monarch Money',
  unknown: 'Unknown',
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function FinanceAppImportDialog({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload')
  const [preview, setPreview] = useState<FinanceCsvPreviewResult | null>(null)
  const [applyResult, setApplyResult] = useState<FinanceCsvApplyResult | null>(null)
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const previewMutation = useMutation({
    mutationFn: (f: File) => importApi.financePreview(f),
    onSuccess: (res) => {
      setPreview(res.data)
      setStep('preview')
      setError(null)
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to parse CSV file')
    },
  })

  const applyMutation = useMutation({
    mutationFn: ({ f, skip }: { f: File; skip: boolean }) =>
      importApi.financeApply(f, skip),
    onSuccess: (res) => {
      setApplyResult(res.data)
      setStep('done')
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to import transactions')
    },
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) {
      setFile(f)
      setError(null)
    }
  }

  function handlePreview() {
    if (file) previewMutation.mutate(file)
  }

  function handleApply() {
    if (file) applyMutation.mutate({ f: file, skip: skipDuplicates })
  }

  function handleClose(open: boolean) {
    if (!open) {
      setFile(null)
      setStep('upload')
      setPreview(null)
      setApplyResult(null)
      setError(null)
    }
    onOpenChange(open)
  }

  const newRowCount = preview ? preview.rows.filter(r => !r.isDuplicate).length : 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import from Finance App
            {preview && (
              <Badge variant="secondary" className="ml-2">
                {FORMAT_LABELS[preview.format] || preview.format}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload a CSV export from YNAB, Mint, or Monarch Money.'}
            {step === 'preview' && `Found ${preview?.totalRows} rows — review before importing.`}
            {step === 'done' && 'Import complete.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload step */}
          {step === 'upload' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Supported formats: <strong>YNAB</strong>, <strong>Mint / Intuit</strong>, <strong>Monarch Money</strong>
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
              />
              {file && (
                <p className="text-sm text-muted-foreground">Selected: {file.name}</p>
              )}
            </div>
          )}

          {/* Preview step */}
          {step === 'preview' && preview && (
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <span><strong>{preview.totalRows}</strong> rows found</span>
                <span className="text-amber-600"><strong>{preview.duplicates}</strong> duplicates</span>
                {preview.parseErrors > 0 && (
                  <span className="text-red-500"><strong>{preview.parseErrors}</strong> parse errors</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="skip-dupes"
                  checked={skipDuplicates}
                  onCheckedChange={(v) => setSkipDuplicates(Boolean(v))}
                />
                <Label htmlFor="skip-dupes" className="text-sm">
                  Skip duplicates (recommended)
                </Label>
              </div>

              <div className="overflow-x-auto rounded border max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Date</th>
                      <th className="px-2 py-1.5 text-left">Merchant</th>
                      <th className="px-2 py-1.5 text-right">Amount</th>
                      <th className="px-2 py-1.5 text-left">Category</th>
                      <th className="px-2 py-1.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.rowIndex} className={row.isDuplicate ? 'opacity-50' : ''}>
                        <td className="px-2 py-1">{row.date}</td>
                        <td className="px-2 py-1 truncate max-w-[160px]">{row.merchant}</td>
                        <td className={`px-2 py-1 text-right ${row.amount >= 0 ? 'text-green-600' : ''}`}>
                          {formatCurrency(Math.abs(row.amount))}
                        </td>
                        <td className="px-2 py-1 text-muted-foreground">{row.category_name || '—'}</td>
                        <td className="px-2 py-1 text-center">
                          {row.isDuplicate ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Duplicate</Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600 border-green-300 text-[10px]">New</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.totalRows > 200 && (
                  <p className="text-xs text-center text-muted-foreground p-2">
                    Showing first 200 of {preview.totalRows} rows
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Done step */}
          {step === 'done' && applyResult && (
            <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-400">
                  Successfully imported {applyResult.imported} transactions
                </p>
                {applyResult.skipped > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {applyResult.skipped} duplicates skipped
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button
                onClick={handlePreview}
                disabled={!file || previewMutation.isPending}
              >
                {previewMutation.isPending ? 'Analyzing…' : 'Preview'}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
              <Button
                onClick={handleApply}
                disabled={applyMutation.isPending}
              >
                {applyMutation.isPending ? 'Importing…' : `Import ${newRowCount} Transactions`}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={() => handleClose(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
